/**
 * Main recurring transaction detector
 * Orchestrates all detection logic using modular analyzers
 */

import type { Transaction } from '../../../types/transaction';
import { DETECTION_CONFIG, type RecurringFrequency, type RecurringType } from './config';
import {
  normalizeDescription,
  extractMerchant,
  shouldSkipDescription,
  createMerchantGroupState,
  getGroupKey
} from './normalizer';
import { analyzeIntervals } from './intervalAnalyzer';
import { analyzeAmounts } from './amountAnalyzer';

export type { RecurringFrequency, RecurringType } from './config';

/**
 * Recurring pattern detected from transactions
 */
export interface RecurringPattern {
  /** Unique pattern identifier (normalized description) */
  pattern: string;

  /** Human-readable display name */
  displayName: string;

  /** Number of occurrences */
  occurrences: number;

  /** Average interval between transactions in days */
  avgInterval: number;

  /** Detected frequency (weekly, monthly, etc.) */
  frequency: RecurringFrequency;

  /** Average transaction amount */
  avgAmount: number;

  /** Minimum transaction amount */
  minAmount: number;

  /** Maximum transaction amount */
  maxAmount: number;

  /** Type of recurring transaction */
  recurringType: RecurringType;

  /** Confidence score (0-1) */
  confidence: number;

  /** Date of first transaction */
  firstDate: string;

  /** Date of last transaction */
  lastDate: string;

  /** Category from transactions */
  category: string;

  /** Transaction IDs that make up this pattern */
  transactions: string[];
}

/**
 * Detection options
 */
export interface DetectionOptions {
  /** Minimum number of occurrences to consider recurring */
  minOccurrences?: number;

  /** Days of tolerance for interval consistency (±N days) */
  intervalTolerance?: number;

  /** Percentage amount variance allowed (0.15 = 15%) */
  amountTolerance?: number;

  /** Minimum days between occurrences (avoid daily transactions) */
  minInterval?: number;

  /** Maximum days between occurrences (monthly = 28-31 days) */
  maxInterval?: number;
}

/**
 * Transaction with normalized description for internal use
 */
interface NormalizedTransaction extends Transaction {
  normalizedDesc: string;
}

/**
 * Detect recurring transaction patterns
 *
 * @param transactions - Array of all transactions
 * @param options - Detection options (uses defaults from config if not specified)
 * @returns Array of detected recurring patterns, sorted by confidence
 */
export function detectRecurringTransactions(
  transactions: Transaction[],
  options: DetectionOptions = {}
): RecurringPattern[] {
  // Use provided options or fall back to config defaults
  const effectiveOptions = {
    minOccurrences: options.minOccurrences ?? DETECTION_CONFIG.thresholds.minOccurrences,
    intervalTolerance: options.intervalTolerance ?? DETECTION_CONFIG.thresholds.intervalTolerance,
    amountTolerance: options.amountTolerance ?? DETECTION_CONFIG.thresholds.amountTolerance,
    minInterval: options.minInterval ?? DETECTION_CONFIG.thresholds.minInterval,
    maxInterval: options.maxInterval ?? DETECTION_CONFIG.thresholds.maxInterval
  };

  // Group transactions by merchant/pattern
  const groups = groupTransactionsByMerchant(transactions);

  // Analyze each group for recurring patterns
  const recurringPatterns: RecurringPattern[] = [];

  for (const [groupKey, groupTransactions] of Object.entries(groups)) {
    const pattern = analyzeGroup(groupKey, groupTransactions, effectiveOptions);
    if (pattern) {
      recurringPatterns.push(pattern);
    }
  }

  // Sort by confidence descending
  recurringPatterns.sort((a, b) => b.confidence - a.confidence);

  return recurringPatterns;
}

/**
 * Group transactions by normalized merchant name
 * Uses fuzzy grouping to handle transaction description variations
 */
function groupTransactionsByMerchant(
  transactions: Transaction[]
): Record<string, NormalizedTransaction[]> {
  const groups: Record<string, NormalizedTransaction[]> = {};
  const groupState = createMerchantGroupState();

  transactions.forEach(transaction => {
    // Skip income, excluded, and already-recurring transactions
    if (transaction.amount > 0 || transaction.excluded || transaction.isRecurring) {
      return;
    }

    const normalized = normalizeDescription(transaction.description);

    // Skip very generic descriptions
    if (shouldSkipDescription(normalized)) {
      return;
    }

    const merchant = extractMerchant(normalized);
    const key = getGroupKey(groupState, merchant);

    if (!groups[key]) {
      groups[key] = [];
    }

    groups[key].push({
      ...transaction,
      normalizedDesc: normalized
    });
  });

  return groups;
}

/**
 * Analyze a group of transactions for recurring pattern
 */
function analyzeGroup(
  groupKey: string,
  groupTransactions: NormalizedTransaction[],
  options: Required<DetectionOptions>
): RecurringPattern | null {
  // Need at least 2 for quarterly/yearly, or minOccurrences for others
  if (groupTransactions.length < 2) {
    return null;
  }

  // Sort by date
  groupTransactions.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Analyze intervals
  const dates = groupTransactions.map(t => t.date);
  const intervalAnalysis = analyzeIntervals(dates);

  // Check if we have a valid frequency
  if (!intervalAnalysis.frequency) {
    return null; // Doesn't match any known pattern
  }

  // For quarterly/yearly, allow with just 2 occurrences if consistent
  const isLongInterval = intervalAnalysis.frequency === 'quarterly' || intervalAnalysis.frequency === 'yearly';
  const minRequired = isLongInterval ? 2 : options.minOccurrences;

  if (groupTransactions.length < minRequired) {
    return null;
  }

  // Check interval consistency
  if (!intervalAnalysis.isConsistent) {
    return null;
  }

  // Analyze amounts
  const amounts = groupTransactions.map(t => Math.abs(t.amount));
  const amountAnalysis = analyzeAmounts(amounts);

  if (!amountAnalysis.isConsistent) {
    return null;
  }

  // Infer recurring type based on description keywords
  const recurringType = inferRecurringType(groupKey);

  // Calculate confidence score
  const confidence = calculateConfidence(
    intervalAnalysis,
    amountAnalysis,
    groupTransactions.length
  );

  // Get a representative description (use the shortest, most common one)
  const representativeDesc = getRepresentativeDescription(
    groupTransactions.map(t => t.description)
  );

  return {
    pattern: groupKey,
    displayName: representativeDesc,
    occurrences: groupTransactions.length,
    avgInterval: intervalAnalysis.avgInterval,
    frequency: intervalAnalysis.frequency,
    avgAmount: amountAnalysis.avgAmount,
    minAmount: amountAnalysis.minAmount,
    maxAmount: amountAnalysis.maxAmount,
    recurringType,
    confidence,
    firstDate: groupTransactions[0].date,
    lastDate: groupTransactions[groupTransactions.length - 1].date,
    category: groupTransactions[0].category,
    transactions: groupTransactions.map(t => t.id)
  };
}

/**
 * Infer if this is a bill or subscription based on keywords
 */
function inferRecurringType(normalizedDesc: string): RecurringType {
  const billKeywords = DETECTION_CONFIG.billKeywords;

  for (const keyword of billKeywords) {
    if (normalizedDesc.includes(keyword)) {
      return 'bill';
    }
  }

  return 'subscription';
}

/**
 * Calculate confidence score (0-1)
 * Based on interval consistency, amount consistency, and number of occurrences
 */
function calculateConfidence(
  intervalAnalysis: ReturnType<typeof analyzeIntervals>,
  amountAnalysis: ReturnType<typeof analyzeAmounts>,
  occurrenceCount: number
): number {
  const weights = DETECTION_CONFIG.confidenceWeights;

  // Interval score: based on how many intervals are in normal range
  const intervalScore = intervalAnalysis.intervals.length > 0
    ? intervalAnalysis.breakdown.normal.length / intervalAnalysis.intervals.length
    : 0;

  // Amount score: inverse of variance relative to average
  let amountScore = 1.0;
  if (amountAnalysis.uniquePriceCount > 1) {
    const variance = (amountAnalysis.maxAmount - amountAnalysis.minAmount) / amountAnalysis.avgAmount;
    amountScore = Math.max(0, 1 - variance);
  }

  // Occurrence score: more occurrences = higher confidence (max out at 6)
  const occurrenceScore = Math.min(1, occurrenceCount / 6);

  // Weighted average
  const confidence =
    intervalScore * weights.interval +
    amountScore * weights.amount +
    occurrenceScore * weights.occurrence;

  return Math.max(0, Math.min(1, confidence));
}

/**
 * Get the best representative description from a list
 * Prefers shorter, cleaner descriptions
 */
function getRepresentativeDescription(descriptions: string[]): string {
  if (descriptions.length === 0) {
    return 'Unknown';
  }

  // Return the shortest description (often cleaner)
  return descriptions.reduce((shortest, current) =>
    current.length < shortest.length ? current : shortest
  );
}

/**
 * Get suggested categorization rule from a recurring pattern
 */
export function getSuggestedRule(pattern: RecurringPattern) {
  return {
    pattern: pattern.pattern,
    category: pattern.category,
    normalizedName: pattern.displayName,
    markAsRecurring: true,
    recurringType: pattern.recurringType
  };
}

// Re-export for backward compatibility
export { normalizeDescription } from './normalizer';
